import { useEffect, useState } from "react";
import type { DiamondManagerSettings } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { CompactCheck, CompactSelect, CompactSlider, CompactText, MiniBtn, Row } from "../../inspector/rows";
import { pickFile } from "../../../../util/download";

const ENV_SLOT_OPTIONS = [
  { id: "0", label: "Primary" },
  { id: "1", label: "Environment 2" },
  { id: "2", label: "Environment 3" },
];

/** Which in-flight action owns the panel's single pending slot. */
type Job = "makeDiamond" | "makeStandard" | "envFile" | "env2" | "env3" | "cache" | "settings";

const msgOf = (e: unknown) => (e instanceof Error && e.message) || "Failed — see console.";

/** Diamond manager (v19 + v26 env slots + v31 resident slots, iJewel Diamonds
 * panel): convert selection to/from the diamond shader, dedicated gem-env
 * (primary + 2 extra RESIDENT slots), oriented-env flag, env rotation,
 * per-group env slot selection, auto-instance, clear cache. */
export function DiamondsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const store = useEditorStore();
  const groupId = useDomainState("selection") as string | null;
  const [s, setS] = useState<DiamondManagerSettings | null>(null);
  const [envSlot, setEnvSlot] = useState<0 | 1 | 2>(0);
  const [pending, setPending] = useState<Job | null>(null);
  const [err, setErr] = useState<{ job: Job; message: string } | null>(null);
  const [mountErr, setMountErr] = useState<string | null>(null);
  useEffect(() => { r.getDiamondSettings().then(setS).catch((e: unknown) => setMountErr(msgOf(e))); }, [r]);
  // Per-group slot has no getter on the contract (set-only RPC) — reset the
  // local picker back to Primary whenever the selection changes.
  useEffect(() => { setEnvSlot(0); }, [groupId]);
  const set = (p: Partial<DiamondManagerSettings>) => {
    setErr(null);
    return r.setDiamondSettings(p).then(setS).catch((e: unknown) => setErr({ job: "settings", message: msgOf(e) }));
  };

  /**
   * Hold one pending slot across an action. Gem-env loads are a network fetch
   * plus an HDR decode plus a re-bake of every diamond material — seconds of
   * work during which the buttons used to stay enabled with the promise thrown
   * away by `void`, so a second click queued a second decode and a failure was
   * invisible.
   */
  const run = async (job: Job, work: () => Promise<unknown>) => {
    if (pending) return;
    setPending(job);
    setErr(null);
    try {
      await work();
    } catch (e) {
      setErr({ job, message: msgOf(e) });
    } finally {
      setPending(null);
    }
  };

  /** Pull the manager settings back after anything that changes the live gem env. */
  const resync = async () => { setS(await r.getDiamondSettings()); };

  // v31: env2Url/env3Url are RESIDENT gem-env textures (kept loaded alongside
  // the primary), not swapped in directly. Activating a slot as the PRIMARY
  // env uses the dedicated activateDiamondEnvSlot RPC; pre-v31 there's no
  // resident concept, so fall back to the old swap-via-URL behavior.
  const useEnvSlot = (job: Job, slot: 1 | 2, url: string | undefined) =>
    run(job, async () => {
      if (!url) return;
      if (caps.v31) await r.activateDiamondEnvSlot(slot);
      else await r.loadDiamondEnvFromUrl(url);
      await resync();
    });

  /**
   * Reclassifying a group metal↔gem emits no viewer event, and the playground
   * only re-reads `material`/`groups` on selectionChanged — so without these
   * refreshes the inspector kept rendering the old material kind (metal sliders
   * over a group that is now a gem, firing setGroupMetalParam at it).
   */
  const reclassify = (job: Job, call: () => Promise<unknown>) =>
    run(job, async () => {
      await call();
      await Promise.all([store.refresh("material"), store.refresh("groups")]);
    });

  const errFor = (job: Job) =>
    err && err.job === job ? <div className="px-3 pb-1.5 text-[11px] text-ed-red">{err.message}</div> : null;

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          primary
          icon="diamond"
          busy={pending === "makeDiamond"}
          disabled={pending !== null}
          onClick={() => void reclassify("makeDiamond", () => r.makeDiamond())}
        >
          Make Diamond
        </MiniBtn>
        <MiniBtn
          icon="category"
          busy={pending === "makeStandard"}
          disabled={pending !== null}
          onClick={() => void reclassify("makeStandard", () => r.makeStandard())}
        >
          Make Standard
        </MiniBtn>
      </div>
      {errFor("makeDiamond")}
      {errFor("makeStandard")}
      {s ? (
        <>
          <Row label="Use Scene Environment"><CompactCheck checked={s.forceSceneEnvironment} onChange={(v) => void set({ forceSceneEnvironment: v })} /></Row>
          <Row label="Oriented Env Map"><CompactCheck checked={!!s.diamondOrientedEnvMap} onChange={(v) => void set({ diamondOrientedEnvMap: v })} /></Row>
          <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
            <MiniBtn
              icon="image"
              busy={pending === "envFile"}
              disabled={pending !== null}
              onClick={() => void run("envFile", async () => {
                // pickFile resolves only once `onFile` settles, but it swallows
                // an onFile rejection, so carry the failure out by hand.
                let failure: unknown;
                await pickFile(".hdr,.exr,.jpg,.png", async (f) => {
                  try {
                    await r.loadDiamondEnvFromFile(f);
                    await resync();
                  } catch (e) {
                    failure = e;
                  }
                });
                if (failure) throw failure;
              })}
            >
              Environment
            </MiniBtn>
          </div>
          {errFor("envFile")}
          <Row label="Env Rotation"><CompactSlider value={s.envMapRotation} min={0} max={6.283} step={0.01} onChange={(v) => void set({ envMapRotation: v })} /></Row>
          <Row label="Environment 2"><CompactText value={s.env2Url ?? ""} placeholder="HDR URL" onChange={(v) => void set({ env2Url: v })} /></Row>
          <Row label="Environment 3"><CompactText value={s.env3Url ?? ""} placeholder="HDR URL" onChange={(v) => void set({ env3Url: v })} /></Row>
          <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
            <MiniBtn
              icon="filter_2"
              busy={pending === "env2"}
              // With no URL the click was a silent no-op (`if (!url) return`).
              disabled={pending !== null || !s.env2Url}
              onClick={() => void useEnvSlot("env2", 1, s.env2Url)}
            >
              Use Env 2
            </MiniBtn>
            <MiniBtn
              icon="filter_3"
              busy={pending === "env3"}
              disabled={pending !== null || !s.env3Url}
              onClick={() => void useEnvSlot("env3", 2, s.env3Url)}
            >
              Use Env 3
            </MiniBtn>
          </div>
          {s.env2Url && s.env3Url ? null : (
            <div className="px-3 pb-1.5 text-[11px] text-ed-text-3">Enter an HDR URL above to enable its slot.</div>
          )}
          {errFor("env2")}
          {errFor("env3")}
          {caps.v31 && groupId ? (
            <Row label="Env Slot (selected)" hint="Which resident gem-env texture the selected group's stones sample.">
              <CompactSelect
                value={String(envSlot)}
                options={ENV_SLOT_OPTIONS}
                onChange={(v) => {
                  const slot = Number(v) as 0 | 1 | 2;
                  setEnvSlot(slot);
                  setErr(null);
                  // A rejected slot switch used to leave the picker showing a
                  // slot the stones never actually moved to.
                  r.setGroupGemEnvSlot(groupId, slot).catch((e: unknown) => {
                    setEnvSlot(0);
                    setErr({ job: "settings", message: msgOf(e) });
                  });
                }}
              />
            </Row>
          ) : null}
          <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
            <MiniBtn
              icon="delete_sweep"
              busy={pending === "cache"}
              disabled={pending !== null}
              onClick={() => void run("cache", () => r.clearDiamondCache())}
            >
              Clear Cache
            </MiniBtn>
          </div>
          <div className="px-3 pb-1.5 text-[11px] text-ed-text-3">Auto-instancing is not exposed by this viewer build.</div>
          {errFor("cache")}
          {errFor("settings")}
        </>
      ) : mountErr ? (
        <div className="px-3 pb-1.5 text-[11px] text-ed-red">{mountErr}</div>
      ) : null}
    </div>
  );
}
